import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Button, TextField, Dialog, DialogContent, DialogActions, IconButton, Stack,
  Autocomplete, MenuItem, Select, FormControl, InputLabel, InputAdornment, Checkbox, ListItemText,
  OutlinedInput,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

/**
 * Shared filter modal for the Products and Sales lists. Callers describe the
 * form with a `fields` array and own the applied `filters` object; the dialog
 * edits a draft copy and only calls `onApply` when "Apply filters" is pressed.
 *
 * Field shapes (all take `label`):
 *   { type: 'multiselect', key, options: [{value,label}], anyLabel }
 *   { type: 'select',      key, options: [{value,label}], placeholder }
 *   { type: 'autocomplete',key, options: [string], placeholder }
 *   { type: 'range',       minKey, maxKey, prefix }       numeric min/max
 *   { type: 'dateRange',   startKey, endKey }             YYYY-MM-DD inputs
 *   { type: 'text',        key, placeholder }
 */

const isSet = (v) => (Array.isArray(v) ? v.length > 0 : v !== '' && v != null && v !== false);

export const countActiveFilters = (filters) => Object.values(filters).filter(isSet).length;

/** Numeric min/max check; blank bounds are open. Null values never match an active bound. */
export function inNumberRange(value, min, max) {
  if (min === '' && max === '') return true;
  if (value == null || Number.isNaN(value)) return false;
  const lo = min !== '' ? parseFloat(min) : -Infinity;
  const hi = max !== '' ? parseFloat(max) : Infinity;
  return value >= lo && value <= hi;
}

/** Inclusive check of a local "YYYY-MM-DD" day against optional start/end days. */
export function inDayRange(day, start, end) {
  if (!start && !end) return true;
  if (!day) return false;
  if (start && day < start) return false;
  if (end && day > end) return false;
  return true;
}

export function containsText(haystack, needle) {
  return String(haystack || '').toLowerCase().includes(String(needle).toLowerCase());
}

function RangeRow({ label, children }) {
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>{label}</Typography>
      <Stack direction="row" spacing={1} alignItems="center">{children}</Stack>
    </Box>
  );
}

function FieldInput({ field, draft, setField }) {
  switch (field.type) {
    case 'multiselect': {
      const selected = draft[field.key];
      return (
        <FormControl size="small" fullWidth>
          <InputLabel id={`f-${field.key}`}>{field.label}</InputLabel>
          <Select
            labelId={`f-${field.key}`}
            label={field.label}
            multiple
            displayEmpty
            value={selected}
            onChange={(e) => setField(field.key, e.target.value)}
            input={<OutlinedInput label={field.label} />}
            renderValue={(sel) => (
              sel.length === 0
                ? (field.anyLabel || 'Any')
                : field.options.filter((o) => sel.includes(o.value)).map((o) => o.label).join(', ')
            )}
          >
            {field.options.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                <Checkbox size="small" checked={selected.includes(o.value)} />
                <ListItemText primary={o.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }
    case 'select':
      return (
        <FormControl size="small" fullWidth>
          <InputLabel id={`f-${field.key}`}>{field.label}</InputLabel>
          <Select
            labelId={`f-${field.key}`}
            label={field.label}
            displayEmpty
            value={draft[field.key]}
            onChange={(e) => setField(field.key, e.target.value)}
          >
            <MenuItem value=""><em>{field.placeholder || 'Any'}</em></MenuItem>
            {field.options.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>
      );
    case 'autocomplete':
      return (
        <Autocomplete
          size="small"
          options={field.options}
          value={draft[field.key] || null}
          onChange={(_, val) => setField(field.key, val || '')}
          renderInput={(params) => <TextField {...params} label={field.label} placeholder={field.placeholder} size="small" />}
        />
      );
    case 'range': {
      const adornment = field.prefix
        ? { startAdornment: <InputAdornment position="start">{field.prefix}</InputAdornment> }
        : undefined;
      return (
        <RangeRow label={field.label}>
          <TextField size="small" fullWidth type="number" placeholder="Min" value={draft[field.minKey]}
            onChange={(e) => setField(field.minKey, e.target.value)} InputProps={adornment} />
          <Typography color="text.secondary">-</Typography>
          <TextField size="small" fullWidth type="number" placeholder="Max" value={draft[field.maxKey]}
            onChange={(e) => setField(field.maxKey, e.target.value)} InputProps={adornment} />
        </RangeRow>
      );
    }
    case 'dateRange':
      return (
        <RangeRow label={field.label}>
          <TextField size="small" fullWidth type="date" value={draft[field.startKey]}
            onChange={(e) => setField(field.startKey, e.target.value)} InputLabelProps={{ shrink: true }} />
          <Typography color="text.secondary">-</Typography>
          <TextField size="small" fullWidth type="date" value={draft[field.endKey]}
            onChange={(e) => setField(field.endKey, e.target.value)} InputLabelProps={{ shrink: true }} />
        </RangeRow>
      );
    case 'text':
      return (
        <TextField size="small" fullWidth label={field.label} placeholder={field.placeholder}
          value={draft[field.key]} onChange={(e) => setField(field.key, e.target.value)} />
      );
    default:
      return null;
  }
}

export default function FilterDialog({ open, onClose, fields, filters, emptyFilters, onApply, subtitle = 'Show items that match all filters.' }) {
  const [draft, setDraft] = useState(filters);

  // Start each opening from the currently applied filters.
  useEffect(() => { if (open) setDraft(filters); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const setField = (key, value) => setDraft((prev) => ({ ...prev, [key]: value }));
  const draftCount = useMemo(() => countActiveFilters(draft), [draft]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', px: 3, pt: 3 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Filters</Typography>
          <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
        </Box>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </Box>
      <Box sx={{ px: 3, pt: 1, display: 'flex', justifyContent: 'flex-end' }}>
        <Button size="small" onClick={() => setDraft(emptyFilters)}>Clear all</Button>
      </Box>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
        {fields.map((field) => (
          <FieldInput key={field.key || field.minKey || field.startKey} field={field} draft={draft} setField={setField} />
        ))}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button fullWidth variant="contained" onClick={() => { onApply(draft); onClose(); }}>
          Apply filters{draftCount > 0 ? ` (${draftCount})` : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
